import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

export interface CustomerUser {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: 'customer_admin' | 'customer_manager' | 'customer_viewer';
    status: 'active' | 'deactivated';
    createdAt: string;
    updatedAt: string;
    createdBy: string;
}

export interface TenantInfo {
    id: number;
    name: string;
    branding: {
        logo: string;
        primaryColor: string;
        secondaryColor: string;
    };
    settings: {
        allowUserRegistration: boolean;
        defaultLanguage: string;
        timezone: string;
    };
    users?: CustomerUser[];
}

interface TenantState {
    currentTenant: TenantInfo | null;
}

const initialState: TenantState = {
    currentTenant: {
        id: 1,
        name: 'Tenant A',
        branding: {
            logo: 'https://via.placeholder.com/150',
            primaryColor: '#3B82F6',
            secondaryColor: '#10B981',
        },
        settings: {
            allowUserRegistration: true,
            defaultLanguage: 'en',
            timezone: 'UTC',
        },
        users: [
            {
                id: '1',
                email: 'admin@tenanta.com',
                firstName: 'John',
                lastName: 'Doe',
                role: 'customer_admin',
                status: 'active',
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                createdBy: 'system'
            },
            {
                id: '2',
                email: 'manager@tenanta.com',
                firstName: 'Jane',
                lastName: 'Smith',
                role: 'customer_manager',
                status: 'active',
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                createdBy: '1'
            }
        ],
    },
};

const tenantSlice = createSlice({
    name: 'tenant',
    initialState,
    reducers: {
        setCurrentTenant: (state, action: PayloadAction<TenantInfo>) => {
            state.currentTenant = action.payload;
        },
        updateBranding: (state, action: PayloadAction<Partial<TenantInfo['branding']>>) => {
            if (state.currentTenant) {
                state.currentTenant.branding = { ...state.currentTenant.branding, ...action.payload };
            }
        },
        updateTenantSettings: (state, action: PayloadAction<Partial<TenantInfo['settings']>>) => {
            if (state.currentTenant) {
                state.currentTenant.settings = { ...state.currentTenant.settings, ...action.payload };
            }
        },
        addUser: (state, action: PayloadAction<CustomerUser>) => {
            if (state.currentTenant?.users) {
                state.currentTenant.users.push(action.payload);
            }
        },
        updateUser: (state, action: PayloadAction<CustomerUser>) => {
            if (state.currentTenant?.users) {
                const index = state.currentTenant.users.findIndex(u => u.id === action.payload.id);
                if (index !== -1) {
                    state.currentTenant.users[index] = action.payload;
                }
            }
        },
        deactivateUser: (state, action: PayloadAction<string>) => {
            const user = state.currentTenant?.users?.find(u => u.id === action.payload);
            if (user) user.status = 'deactivated';
        },
        reactivateUser: (state, action: PayloadAction<string>) => {
            const user = state.currentTenant?.users?.find(u => u.id === action.payload);
            if (user) user.status = 'active';
        },
        updateUserRole: (state, action: PayloadAction<{ userId: string; role: CustomerUser['role'] }>) => {
            const user = state.currentTenant?.users?.find(u => u.id === action.payload.userId);
            if (user) user.role = action.payload.role;
        },
    },
});

export const {
    setCurrentTenant,
    updateBranding,
    updateTenantSettings,
    addUser,
    updateUser,
    deactivateUser,
    reactivateUser,
    updateUserRole
} = tenantSlice.actions;

export default tenantSlice.reducer;